import { BookingClientService } from '@services/api/booking/booking-client.service';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { IApiQueryOptions } from '@services/api/models/api-query-params.model';
import { HardCodedConfigService } from '@services/util/hard-coded-config.service';
import { addDays, format } from 'date-fns';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import { ArrivalBookingRow, ConfirmableBookingRow, DepartureBookingRow, GuestBookingRow } from '../models/booking-overview.model';
import { BookingMapperService } from './booking-mapper.service';

/**
 * Service to fetch booking data from API and return view models
 * Uses specialized services for mapping and other operations
 */
@Injectable({
  providedIn: 'root'
})
export class BookingOverviewService {
  private readonly bookingClientService = inject(BookingClientService);
  private readonly config = inject(HardCodedConfigService);
  private readonly bookingMapperService = inject(BookingMapperService);

  /**
   * Get all confirmable bookings within the lookahead window (today … today + lookaheadDays).
   * @param lookaheadDays Upper bound offset in days from today (host-configurable, default from config).
   */
  getConfirmableBookings(lookaheadDays: number = this.config.CONFIRMABLE_LOOKAHEAD_DEFAULT_DAYS): Observable<ConfirmableBookingRow[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const upperBound = format(addDays(new Date(), lookaheadDays), 'yyyy-MM-dd');
    const queryOptions: Omit<IApiQueryOptions, 'pagination'> = {
      filters: {
        'bookingStatus.id': this.config.BOOKING_STATUS_CONFIRMABLE_ID,
        'start_date[after]': today,
        'start_date[before]': upperBound
      },
      order: {
        id: 'asc'
      }
    };

    return this.bookingClientService
      .getAllItems('', queryOptions)
      .pipe(map((bookings: IBookingModel[]) => bookings.map((booking) => this.bookingMapperService.mapToConfirmableBookingRow(booking))));
  }

  /**
   * Get all arrivals for today
   * @returns Observable with list of ArrivalBookingRow
   */
  getArrivals(): Observable<ArrivalBookingRow[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const queryOptions: Omit<IApiQueryOptions, 'pagination'> = {
      filters: {
        start_date: today,
        'bookingStatus.id': this.config.BOOKING_STATUS_FINALIZED_ID
      },
      order: {
        id: 'asc'
      }
    };

    return this.bookingClientService
      .getAllItems('', queryOptions)
      .pipe(map((bookings: IBookingModel[]) => bookings.map((booking) => this.bookingMapperService.mapToArrivalBookingRow(booking))));
  }

  /**
   * Get all departures for today
   * @returns Observable with list of DepartureBookingRow
   */
  getDepartures(): Observable<DepartureBookingRow[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const queryOptions: Omit<IApiQueryOptions, 'pagination'> = {
      filters: {
        end_date: today,
        'bookingStatus.id': this.config.BOOKING_STATUS_FINALIZED_ID
      },
      order: {
        id: 'asc'
      }
    };

    return this.bookingClientService
      .getAllItems('', queryOptions)
      .pipe(map((bookings: IBookingModel[]) => bookings.map((booking) => this.bookingMapperService.mapToDepartureBookingRow(booking))));
  }

  /**
   * Get all guests currently staying (start_date ≤ today AND end_date ≥ today)
   * @returns Observable with list of GuestBookingRow
   */
  getGuests(): Observable<GuestBookingRow[]> {
    const today = format(new Date(), 'yyyy-MM-dd');
    const queryOptions: Omit<IApiQueryOptions, 'pagination'> = {
      filters: {
        'bookingStatus.id': this.config.BOOKING_STATUS_FINALIZED_ID
      },
      dateFilters: {
        // Get all bookings with start_date <= today AND end_date >= today
        start_date: { date: new Date(today), operator: 'strictly_before' },
        end_date: { date: new Date(today), operator: 'strictly_after' }
      },
      order: {
        id: 'asc'
      }
    };

    return this.bookingClientService
      .getAllItems('', queryOptions)
      .pipe(map((bookings: IBookingModel[]) => bookings.map((booking) => this.bookingMapperService.mapToGuestBookingRow(booking))));
  }
}
